// src/hooks/useAlbumStorage.js
// React hook for non-blocking album storage operations
// 🔧 OPTIMIZED: Skips photo extraction for already-saved albums

import { useCallback, useRef, useState } from 'react';
import { 
  saveAlbum, 
  loadAlbum,
  loadAlbumWithPhotos,
  storePhotosBatch, 
  getPhotosByIds,
  generatePhotoId,
  listAlbums,
  deleteAlbum
} from '../utils/photoStore';

/**
 * Hook for album storage operations
 * Provides non-blocking save/load with progress tracking
 */
export function useAlbumStorage() {
  const saveInProgressRef = useRef(false);
  const saveQueueRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [saveProgress, setSaveProgress] = useState(null);
  
  // 🆕 Track which albums have had their photos stored
  const storedAlbumsRef = useRef(new Set());
  
  /**
   * Save album with photos to IndexedDB
   * 🔧 OPTIMIZED: Skips photo extraction if photos are already stored
   */
  const saveAlbumToStorage = useCallback(async (albumId, albumData, options = {}) => {
    const { 
      onProgress, 
      debounceMs = 2000,
      skipPhotos = false, // Set true if photos are already stored
      forcePhotoStore = false // Set true to re-store photos even if already done
    } = options;
    
    // Clear any pending debounced save
    if (saveQueueRef.current) {
      clearTimeout(saveQueueRef.current);
    }
    
    return new Promise((resolve, reject) => {
      saveQueueRef.current = setTimeout(async () => {
        // Prevent concurrent saves
        if (saveInProgressRef.current) {
          console.log('[useAlbumStorage] Save already in progress, queuing...');
          resolve(false);
          return;
        }
        
        saveInProgressRef.current = true;
        setSaveStatus('saving');
        setSaveProgress({ phase: 'starting', done: 0, total: 0 });
        
        try {
          console.log('[useAlbumStorage] 💾 Starting save for album:', albumId);
          const startTime = Date.now();
          
          // 🔧 CRITICAL OPTIMIZATION: Check if we need to store photos at all
          // If this album has been saved before, photos are already in IndexedDB!
          const photosAlreadyStored = storedAlbumsRef.current.has(albumId) || skipPhotos;
          
          // 🔧 QUICK CHECK: If pages already have photoIds (not photos array with URLs),
          // then photos were already extracted and stored - skip the heavy loop!
          const pagesAlreadyOptimized = (albumData.pages || []).every(page => {
            // Page is optimized if it has photoIds OR if photos array only contains ID references
            if (page.photoIds && page.photoIds.length > 0) return true;
            
            // Check if photos array contains actual data URLs or just references
            const photos = page.photos || [];
            if (photos.length === 0) return true;
            
            // If first photo has an ID and no data URL, it's already optimized
            const firstPhoto = photos[0];
            if (firstPhoto?.id && (!firstPhoto.url || !firstPhoto.url.startsWith('data:'))) {
              return true;
            }
            
            return false;
          });
          
          let photoMapping = new Map();
          
          // 🔧 FAST PATH: Skip photo extraction entirely if already done
          if (photosAlreadyStored || pagesAlreadyOptimized) {
            console.log('[useAlbumStorage] ⚡ Fast path: photos already stored, skipping extraction');
            
            // Just collect existing IDs for the mapping
            (albumData.pages || []).forEach(page => {
              // From photoIds array
              (page.photoIds || []).forEach(id => {
                photoMapping.set(id, id);
              });
              
              // From photos array (ID references)
              (page.photos || []).forEach(photo => {
                if (photo.id) {
                  photoMapping.set(photo.id, photo.id);
                  if (photo.url) photoMapping.set(photo.url, photo.id);
                }
              });
              
              // From items array
              (page.items || []).forEach(item => {
                if (item.id) {
                  photoMapping.set(item.id, item.id);
                  if (item.url) photoMapping.set(item.url, item.id);
                }
              });
            });
            
          } else {
            // 🔧 SLOW PATH: First save - need to extract and store photos
            console.log('[useAlbumStorage] 📸 First save: extracting and storing photos...');
            
            // Step 1: Extract all NEW photos from pages (only data URLs we haven't seen)
            const allPhotos = [];
            const seenUrls = new Set();
            
            (albumData.pages || []).forEach(page => {
              // Photos array
              (page.photos || []).forEach(photo => {
                // Skip photos that already have an ID (already stored)
                if (photo.id && !photo.url?.startsWith('data:')) {
                  photoMapping.set(photo.id, photo.id);
                  return;
                }
                
                const url = photo.url || photo.src;
                if (url && url.startsWith('data:') && !seenUrls.has(url)) {
                  seenUrls.add(url);
                  allPhotos.push({
                    url,
                    gps: photo.gps || photo.coordinates,
                    timestamp: photo.timestamp || photo.dateTaken,
                    fileName: photo.fileName
                  });
                }
              });
              
              // Items array (for grid layouts)
              (page.items || []).forEach(item => {
                // Skip items that already have an ID
                if (item.id && !item.url?.startsWith('data:')) {
                  photoMapping.set(item.id, item.id);
                  return;
                }
                
                const url = item.url;
                if (url && url.startsWith('data:') && !seenUrls.has(url)) {
                  seenUrls.add(url);
                  allPhotos.push({
                    url,
                    gps: item.gps || item.coordinates,
                    timestamp: item.timestamp,
                    fileName: item.fileName
                  });
                }
              });
            });
            
            console.log(`[useAlbumStorage] Found ${allPhotos.length} NEW photos to store`);
            
            // Step 2: Store photos in IndexedDB
            if (allPhotos.length > 0) {
              setSaveProgress({ phase: 'photos', done: 0, total: allPhotos.length });
              
              const newMapping = await storePhotosBatch(allPhotos, albumId, (done, total) => {
                setSaveProgress({ phase: 'photos', done, total });
                onProgress?.({ phase: 'photos', done, total });
              });
              
              // Merge new mapping with existing
              newMapping.forEach((id, url) => photoMapping.set(url, id));
            }
            
            // Mark this album as having photos stored
            storedAlbumsRef.current.add(albumId);
          }
          
          // Step 3: Create lightweight album (photo IDs only)
          setSaveProgress({ phase: 'album', done: 0, total: 1 });
          
          const lightPages = (albumData.pages || []).map(page => {
            // 🔧 If page already has photoIds, preserve them
            if (page.photoIds && page.photoIds.length > 0 && !page.photos?.some(p => p.url?.startsWith('data:'))) {
              return {
                type: page.type,
                templateKey: page.templateKey,
                stepId: page.stepId,
                meta: page.meta,
                photoIds: page.photoIds,
                items: page.items?.map(item => ({
                  ...item,
                  url: undefined // Remove actual data URL
                }))
              };
            }
            
            // Convert photos to ID references
            const photoIds = (page.photos || []).map(photo => {
              const url = photo.url || photo.src;
              return photo.id || photoMapping.get(url) || generatePhotoId(url);
            });
            
            // Convert items to ID references
            const items = (page.items || []).map(item => ({
              ...item,
              id: item.id || photoMapping.get(item.url) || generatePhotoId(item.url),
              url: undefined // Remove actual data URL
            }));
            
            return {
              type: page.type,
              templateKey: page.templateKey,
              stepId: page.stepId,
              meta: page.meta,
              photoIds,
              items: items.length > 0 ? items : undefined
              // Note: 'photos' array is intentionally omitted
            };
          });
          
          // Step 4: Save album metadata + lightweight pages
          await saveAlbum(albumId, {
            ...albumData,
            pages: lightPages,
            photos: undefined // Don't store photos at album level
          });
          
          const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
          console.log(`[useAlbumStorage] ✅ Album saved in ${elapsed}s`);
          
          setSaveStatus('saved');
          setSaveProgress({ phase: 'complete', done: 1, total: 1 });
          onProgress?.({ phase: 'complete' });
          
          // Reset status after a delay
          setTimeout(() => {
            setSaveStatus('idle');
            setSaveProgress(null);
          }, 2000);
          
          resolve(true);
        } catch (err) {
          console.error('[useAlbumStorage] ❌ Save failed:', err);
          setSaveStatus('error');
          setSaveProgress(null);
          reject(err);
        } finally {
          saveInProgressRef.current = false;
        }
      }, debounceMs);
    });
  }, []);
  
  /**
   * Load album from IndexedDB
   * @param {string} albumId 
   * @param {object} options
   * @param {boolean} options.hydratePhotos - If true, load full photo data (for export)
   */
  const loadAlbumFromStorage = useCallback(async (albumId, options = {}) => {
    const { hydratePhotos = false } = options;
    
    try {
      console.log('[useAlbumStorage] 📂 Loading album:', albumId, { hydratePhotos });
      
      if (hydratePhotos) {
        // Full load with photos (for export/share)
        return await loadAlbumWithPhotos(albumId);
      } else {
        // Lightweight load (for editing - photos loaded lazily)
        const album = await loadAlbum(albumId);
        
        // 🆕 Mark this album as having photos stored (since we're loading it)
        if (album) {
          storedAlbumsRef.current.add(albumId);
        }
        
        return album;
      }
    } catch (err) {
      console.error('[useAlbumStorage] ❌ Load failed:', err);
      throw err;
    }
  }, []);
  
  /**
   * Hydrate pages with photo data (for display/export)
   * Call this when you need full photo URLs for a set of pages
   */
  const hydratePages = useCallback(async (pages) => {
    if (!pages || pages.length === 0) return pages;
    
    // Collect all photo IDs
    const photoIds = new Set();
    pages.forEach(page => {
      (page.photoIds || []).forEach(id => photoIds.add(id));
      (page.items || []).forEach(item => {
        if (item.id) photoIds.add(item.id);
      });
    });
    
    if (photoIds.size === 0) return pages;
    
    console.log(`[useAlbumStorage] Hydrating ${photoIds.size} photos...`);
    
    // Load all photos
    const photos = await getPhotosByIds(Array.from(photoIds));
    
    // Reconstruct pages with full photo data
    return pages.map(page => ({
      ...page,
      photos: (page.photoIds || []).map(id => {
        const photo = photos.get(id);
        return photo || { id, url: null };
      }),
      items: (page.items || []).map(item => ({
        ...item,
        url: photos.get(item.id)?.url || item.url
      }))
    }));
  }, []);
  
  /**
   * Get list of all saved albums
   */
  const getSavedAlbums = useCallback(async () => {
    return await listAlbums();
  }, []);
  
  /**
   * Delete an album
   */
  const removeAlbum = useCallback(async (albumId, deletePhotos = false) => {
    // Remove from stored albums tracking
    storedAlbumsRef.current.delete(albumId);
    return await deleteAlbum(albumId, deletePhotos);
  }, []);
  
  /**
   * Cancel any pending save operation
   */
  const cancelSave = useCallback(() => {
    if (saveQueueRef.current) {
      clearTimeout(saveQueueRef.current);
      saveQueueRef.current = null;
    }
    setSaveStatus('idle');
    setSaveProgress(null);
  }, []);
  
  /**
   * 🆕 Mark an album as having photos already stored
   * Call this when you know photos are in IndexedDB (e.g., after initial save)
   */
  const markPhotosStored = useCallback((albumId) => {
    storedAlbumsRef.current.add(albumId);
  }, []);
  
  /**
   * 🆕 Force next save to re-store photos
   * Call this if photos have been modified
   */
  const invalidatePhotos = useCallback((albumId) => {
    storedAlbumsRef.current.delete(albumId);
  }, []);
  
  return {
    // Save/Load functions
    saveAlbumToStorage,
    loadAlbumFromStorage,
    hydratePages,
    getSavedAlbums,
    removeAlbum,
    cancelSave,
    
    // 🆕 Photo tracking
    markPhotosStored,
    invalidatePhotos,
    
    // Status
    saveStatus,
    saveProgress,
    isSaving: saveStatus === 'saving'
  };
}

/**
 * Simplified hook for components that just need to display photos
 */
export function usePhotoLoader() {
  const [loading, setLoading] = useState(false);
  const cacheRef = useRef(new Map());
  
  const loadPhotos = useCallback(async (photoIds) => {
    if (!photoIds || photoIds.length === 0) return new Map();
    
    // Check cache first
    const uncached = photoIds.filter(id => !cacheRef.current.has(id));
    
    if (uncached.length === 0) {
      // All cached
      const result = new Map();
      photoIds.forEach(id => result.set(id, cacheRef.current.get(id)));
      return result;
    }
    
    setLoading(true);
    
    try {
      const loaded = await getPhotosByIds(uncached);
      
      // Update cache
      loaded.forEach((value, key) => {
        cacheRef.current.set(key, value);
      });
      
      // Return all requested photos
      const result = new Map();
      photoIds.forEach(id => {
        result.set(id, cacheRef.current.get(id) || loaded.get(id));
      });
      
      return result;
    } finally {
      setLoading(false);
    }
  }, []);
  
  const clearCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);
  
  return {
    loadPhotos,
    clearCache,
    loading
  };
}

export default useAlbumStorage;